import { useCallback, useMemo, useState } from 'react';
import { Provider } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/provider.pb';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { Button, SpinningLoader, useModalControl } from '@frontend/design-system';
import { AddProviderV3Context, AddProviderV3ContextType } from '../../context/AddProviderV3Context';
import { SETTINGS_TRACKING_IDS } from '../../trackingIds/settings-tracking-ids';
import { AddPractitionerModal } from './AddPractitionerModal';

type Props = {
  locationId: string;
};

export const AddPractitioner = ({ locationId }: Props) => {
  const { t } = useTranslation('schedule-settings');
  const [providerDetails, setProviderDetails] = useState<Provider>({} as Provider);
  const { modalProps, triggerProps } = useModalControl();

  const { hasWSM, isLoading: isLoadingIntegrationDetails } = SchedulerV3.Helpers.useCheckHasWSM(locationId);

  const onModalClose = useCallback(() => {
    setProviderDetails({} as Provider);
    modalProps.onClose();
  }, [modalProps]);

  const providerValue = useMemo<AddProviderV3ContextType>(
    () => ({
      locationId,
      onClose: onModalClose,
      providerDetails,
      setProviderDetails,
    }),
    [locationId, onModalClose, providerDetails]
  );

  if (isLoadingIntegrationDetails) return <SpinningLoader />;

  if (!hasWSM) return null;

  return (
    <AddProviderV3Context.Provider value={providerValue}>
      <Button {...triggerProps} trackingId={SETTINGS_TRACKING_IDS.manageProviders.addProviderButton}>
        {t('Add Provider')}
      </Button>
      <AddPractitionerModal modalProps={modalProps} />
    </AddProviderV3Context.Provider>
  );
};
